import React from "react";
import { Search, X as XIcon } from "lucide-react";

export const SearchBar = ({ value, onChange, placeholder = "Buscar...", testid, className = "" }) => (
  <div className={`brutal-card-sm flex items-center gap-2 px-3 py-2 bg-white flex-1 min-w-[240px] ${className}`}>
    <Search className="w-4 h-4 text-[#555] shrink-0" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="flex-1 outline-none text-sm bg-transparent font-mono-data"
      data-testid={testid}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange("")}
        className="text-[#777] hover:text-[#E60000] shrink-0"
        aria-label="Limpar busca"
        data-testid={testid ? `${testid}-clear` : undefined}
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

// Helper: normaliza (remove acentos e minúsculas) para busca sem case/acento
export const normalize = (str) =>
  (str || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

// Helper: busca em múltiplos campos
export const matchSearch = (item, fields, query) => {
  if (!query) return true;
  const q = normalize(query);
  return fields.some((f) => normalize(item[f]).includes(q));
};

export const StatCard = ({ label, value, sub, accent = false, danger = false, testid }) => (
  <div
    className={`brutal-card p-5 ${accent ? "!bg-[#FF4F00] !text-white" : ""} ${danger ? "!bg-[#3D6B57] !text-white" : ""}`}
    data-testid={testid}
  >
    <div className={`font-mono-data text-[10px] uppercase tracking-widest ${accent || danger ? "text-white/80" : "text-[#777]"}`}>{label}</div>
    <div className="font-display font-black text-3xl mt-2 tracking-tighter">{value}</div>
    {sub && <div className={`mt-2 text-xs font-mono-data ${accent || danger ? "text-white/90" : "text-[#555]"}`}>{sub}</div>}
  </div>
);

export const SectionTitle = ({ children, action }) => (
  <div className="flex items-end justify-between mb-4 border-b-2 border-[#111] pb-2 gap-3">
    <h2 className="font-display font-black text-lg uppercase tracking-tight">{children}</h2>
    {action}
  </div>
);

export const Badge = ({ children, variant = "default", testid }) => {
  const styles = {
    default: "bg-[#3D6B57] text-white",
    success: "bg-[#1A7F37] text-white",
    warn: "bg-[#FFC107] text-[#111]",
    danger: "bg-[#E60000] text-white",
    orange: "bg-[#FF4F00] text-white",
    outline: "bg-white text-[#111] border-2 border-[#111]",
    green: "bg-[#5A8F70] text-white",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest font-mono-data ${styles[variant] || styles.default}`} data-testid={testid}>
      {children}
    </span>
  );
};

export const osStatusBadge = (status) => {
  const m = {
    aberta: { v: "orange", t: "Aberta" },
    em_andamento: { v: "warn", t: "Em Andamento" },
    concluida: { v: "success", t: "Concluída" },
    cancelada: { v: "default", t: "Cancelada" },
  };
  const c = m[status] || m.aberta;
  return <Badge variant={c.v}>{c.t}</Badge>;
};

export const reqStatusBadge = (status) => {
  const m = {
    pendente: { v: "warn", t: "Pendente" },
    aprovada: { v: "green", t: "Aprovada" },
    rejeitada: { v: "danger", t: "Rejeitada" },
    entregue: { v: "success", t: "Entregue" },
    devolvida: { v: "orange", t: "Devolvida" },
    cancelada: { v: "default", t: "Cancelada" },
  };
  const c = m[status] || m.pendente;
  return <Badge variant={c.v}>{c.t}</Badge>;
};

export const Btn = ({ children, variant = "primary", className = "", ...props }) => {
  const v = {
    primary: "bg-[#FF4F00] text-white",
    secondary: "bg-white text-[#111]",
    dark: "bg-[#3D6B57] text-white",
    success: "bg-[#1A7F37] text-white",
    danger: "bg-[#E60000] text-white",
    warn: "bg-[#FFC107] text-[#111]",
  };
  return (
    <button {...props} className={`brutal-btn ${v[variant]} px-4 py-2 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2 ${className}`}>
      {children}
    </button>
  );
};

export const Modal = ({ open, onClose, title, children, size = "lg" }) => {
  if (!open) return null;
  const w = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-2xl", xl: "max-w-3xl", "2xl": "max-w-4xl" }[size];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`brutal-card w-full ${w} max-h-[92vh] overflow-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#3D6B57] text-white px-6 py-3 flex items-center justify-between sticky top-0 z-10">
          <h3 className="font-display font-black uppercase tracking-tight text-sm">{title}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white text-lg leading-none">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

export const Field = ({ label, value, onChange, type = "text", required, testid, placeholder, colspan }) => (
  <div className={colspan ? "col-span-2" : ""}>
    <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">{label}</label>
    <input
      type={type}
      required={required}
      step={type === "number" ? "0.01" : undefined}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border-2 border-[#111] p-2 text-sm outline-none focus:border-[#FF4F00] bg-white"
      data-testid={testid}
    />
  </div>
);

export const SelectField = ({ label, value, onChange, options, required, testid, colspan, placeholder = "— selecione —" }) => (
  <div className={colspan ? "col-span-2" : ""}>
    <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">{label}</label>
    <select
      required={required}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border-2 border-[#111] p-2 text-sm outline-none focus:border-[#FF4F00] bg-white"
      data-testid={testid}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export const TextArea = ({ label, value, onChange, testid, rows = 3 }) => (
  <div className="col-span-2">
    <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">{label}</label>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full border-2 border-[#111] p-2 text-sm outline-none focus:border-[#FF4F00] resize-none bg-white"
      data-testid={testid}
    />
  </div>
);

export const EmptyState = ({ message, cols = 1 }) => (
  <tr><td colSpan={cols} className="text-center py-12 text-[#777] font-mono-data text-sm">{message}</td></tr>
);
